import { z } from 'zod'
import { channelSourceData } from '../../checkout-be/src/schemas/channelSource'
import {
    ContractAttributesSchema,
    itemSchema,
    offerBaseSchema,
    offerPricingKey,
    personSchema,
    sdkBillingAddressSchema,
} from '../../checkout-be/src/schemas/createContractSdk'
import { AchCodes } from '../../checkout-be/src/schemas/initCheckout'

const priceDetailsSchema = z.object({
    taxRate: z.number(),
    netTotalAmount: z.number(),
    taxTotalAmount: z.number(),
    netFirstPaymentAmount: z.number(),
    offerPricingKey: offerPricingKey,
})

export const contractSchema = z.object({
    contractAttributes: ContractAttributesSchema.pick({
        productVariantId: true,
        periodOfCover: true,
        levelOfCoverCode: true,
        effectiveDate: true,
        thirdPartyContractExpiryDate: true,
    })
        .merge(
            z.object({
                cc: priceDetailsSchema.describe(
                    'Credit card price to be shown to customer',
                ),
                ach: priceDetailsSchema
                    .merge(
                        z.object({
                            discountProfileAttributes: z
                                .object({
                                    achDiscountCodeApplied: z
                                        .nativeEnum(AchCodes)
                                        .describe(
                                            'ACH discount code. The discount code that was applied to the contract when paying by ACH, if this is not supplied, then we assume NO discount has been applied. If exists, we assume a discount has been applied and the customer will be shown UI indicating a discount has been applied for this payment method.',
                                        ),
                                    netAmountBeforeDiscount: z
                                        .number()
                                        .describe(
                                            'The net amount before any discount was applied',
                                        ),
                                    netFirstPaymentAmountAfterPromotionalPeriod:
                                        z
                                            .number()
                                            .describe(
                                                'This is the net first monthly payment amount AFTER the promotional period has ended.',
                                            ),
                                })
                                .optional(),
                        }),
                    )
                    .describe(
                        'ACH overide on base price when any discounts are applied',
                    ),
            }),
        )
        .describe('Details of the contract'),
    itemDetails: itemSchema
        .extend({
            description: z.string(),
            brand: z.string(),
        })
        .describe('Details of the item to be purchased'),
})

export const OfferContractSchema = offerBaseSchema.and(
    z.object({
        contracts: z.array(contractSchema).superRefine((contracts, ctx) => {
            const discountCodes: AchCodes[] = []
            contracts.forEach((contract) => {
                if (contract.contractAttributes.ach.discountProfileAttributes) {
                    discountCodes.push(
                        contract.contractAttributes.ach
                            .discountProfileAttributes.achDiscountCodeApplied,
                    )
                }
            })

            if (
                discountCodes.length &&
                !discountCodes.every((val) => val === discountCodes[0])
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'ACH discount codes do not match',
                })
            }
        }),
    }),
)

export const basketSchema = z.object({
    channelSourceData: channelSourceData
        .omit({ userId: true })
        .describe(
            'Identifier for the team and channel that the checkout is being loaded in',
        ),
    offers: z
        .array(OfferContractSchema)
        .describe('Array of Offers to be purchased'),
})

export const customerSchema = z
    .object({
        title: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        address: sdkBillingAddressSchema,
    })
    .and(personSchema)

export const initOptionsSchema = z
    .object({
        authToken: z
            .string()
            .describe(
                'This is the external API token which can be retrieved with a direct lambda invoke',
            ),
        basket: basketSchema.describe(
            'This is the basket of policies to be purchased',
        ),
        customer: customerSchema.describe('Customer Details'),
    })
    .describe('This is the main config object for the SDK')

export type PaymentOption = 'ach' | 'card'

export interface AchFormData {
    accountNumber: string
    routingNumber: string
}

export interface Item {
    itemIterationId: string
    netAmount: number
}

export type InitOptions = z.infer<typeof initOptionsSchema>
export type Contract = z.infer<typeof contractSchema>
